import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for clearing component state caches when user logs out or changes.
 * 
 * Usage:
 * useClearCacheOnLogout(() => {
 *   setMaterials([]);
 *   setQuizzes([]);
 *   setAssignments([]);
 *   setGrades([]);
 *   setAttendance([]);
 * });
 * 
 * @param clearCacheFn - Callback function to call when cache should be cleared
 */
export const useClearCacheOnLogout = (clearCacheFn: () => void) => {
  const { user, isAuthenticated } = useAuth();
  const prevUserIdRef = useRef<string | null>(null);
  const prevAuthStateRef = useRef<boolean>(false);

  useEffect(() => {
    // Check if user changed
    const currentUserId = user?._id || null;
    const userChanged = prevUserIdRef.current !== null && prevUserIdRef.current !== currentUserId;
    
    // Check if logged out (was authenticated, now is not)
    const loggedOut = prevAuthStateRef.current && !isAuthenticated;
    
    // Check if logged in as different user (userId exists and different)
    const differentUserLoggingIn = currentUserId !== null && prevUserIdRef.current !== null && prevUserIdRef.current !== currentUserId;
    
    if (userChanged || loggedOut || differentUserLoggingIn) {
      // Clear cache when user changes or logs out
      clearCacheFn();
    }
    
    // Update refs for next comparison
    prevUserIdRef.current = currentUserId;
    prevAuthStateRef.current = isAuthenticated;
  }, [user?._id, isAuthenticated, clearCacheFn]);
};

/**
 * Clears all localStorage auth-related cache to ensure no stale data persists
 */
export const clearAuthCache = () => {
  try {
    // Remove all auth tokens and user data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('admin_auth_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('staff_auth_token');
    localStorage.removeItem('staff_user');
    
    // Clear session storage admin unlock tokens
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith('admin_tab_pw_')) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach(k => sessionStorage.removeItem(k));
  } catch (e) {
    console.error('[clearAuthCache] Error during cache cleanup:', e);
  }
};
